import { OverlayContainer } from '@angular/cdk/overlay';
import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private renderer: Renderer2;
  private darkThemeKey = 'isDarkTheme';
  private _isDarkTheme = new BehaviorSubject<boolean>(false);

  isDarkTheme$ = this._isDarkTheme.asObservable();

  constructor(rendererFactory: RendererFactory2,
    private overlayContainer: OverlayContainer
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);

    const storedTheme = localStorage.getItem(this.darkThemeKey);

    // DEFAULT = LIGHT THEME
    const isDark = storedTheme === 'true';

    this.setDarkTheme(isDark);
  }

  toggleTheme(): void {
    const isDark = document.body.classList.toggle('dark-theme');
    if (isDark) {
      this.clearUserThemeVariables();
    }
    this._isDarkTheme.next(isDark);
    localStorage.setItem(this.darkThemeKey, String(isDark));
  }

  setDarkTheme(isDark: boolean): void {
    const overlay = this.overlayContainer.getContainerElement();
    if (isDark) {
      this.renderer.removeClass(document.body, 'user-theme');
      this.renderer.removeClass(overlay, 'user-theme');
      this.clearUserThemeVariables();
      this.renderer.addClass(document.body, 'dark-theme');
    } else {
      this.renderer.removeClass(document.body, 'dark-theme');
      // Do not remove 'user-theme' class or clear variables when switching to light
    }
    this._isDarkTheme.next(isDark);
    localStorage.setItem(this.darkThemeKey, String(isDark));
  }

  getCurrentTheme(): boolean {
    return this._isDarkTheme.getValue();
  }

  applyusertheme(formValue: any) {
    const overlay = this.overlayContainer.getContainerElement();
    // ❌ stop if dark mode
    if (document.body.classList.contains('dark-theme')) {
      this.renderer.removeClass(document.body, 'user-theme');
      this.renderer.removeClass(overlay, 'user-theme');
      this.clearUserThemeVariables();
      return;
    }
    this.renderer.removeClass(document.body, 'dark-theme');

    // ✅ enable user theme
    this.renderer.addClass(document.body, 'user-theme');
    const root = document.documentElement;
    // 🔹 Navbar
    root.style.setProperty('--navbar-bg', formValue.navbarBg);
    root.style.setProperty('--navbar-color', formValue.navbarColor);

    // 🔹 Tabs
    root.style.setProperty('--tab-bg', formValue.tabBg);
    root.style.setProperty('--tab-color', formValue.tabColor);
    root.style.setProperty('--tab-active-color', formValue.tabActiveColor);

    // 🔹 Table
    root.style.setProperty('--table-header-bg', formValue.tableHeaderBg);
    root.style.setProperty('--table-header-color', formValue.tableHeaderColor);
    root.style.setProperty('--table-body-color', formValue.tableBodyColor);
    root.style.setProperty('--table-body-bg', formValue.tableBodyBg);
    root.style.setProperty('--table-hover-color', formValue.tableHoverBg);
    root.style.setProperty('--table-footer-bg', formValue.tableFooterBg);
    root.style.setProperty('--table-footer-color', formValue.tableFooterColor);

    // 🔹 Footer
    root.style.setProperty('--footer-bg', formValue.footerBg);
    root.style.setProperty('--footer-color', formValue.footerColor);

    // 🔹 Buttons
    root.style.setProperty('--button-bg', formValue.buttonBg);
    root.style.setProperty('--button-color', formValue.buttonColor);

    // 🔹 Scrollbar
    root.style.setProperty('--scrollbar-color', formValue.scrollbarColor);

    // 🔹 Spinner
    root.style.setProperty('--spinner-bg', formValue.spinnerBg);
    root.style.setProperty('--spinner-color', formValue.spinnerColor);
    root.style.setProperty('--spinner-hover', formValue.spinnerHover);

    // 🔹 Inputs / Controls
    root.style.setProperty('--checkbox-color', formValue.checkboxColor);
    root.style.setProperty('--toggle-color', formValue.toggleColor);

    root.style.setProperty('--icon-color', formValue.iconColor);

    // 🔹 Dialog
    root.style.setProperty('--dialog-bg', formValue.dialogBg);

    // 🔹 Buttons (Action)
    root.style.setProperty('--cancel-btn-color', formValue.cancelBtnColor);
    root.style.setProperty('--save-btn-color', formValue.saveBtnColor);
    root.style.setProperty('--cancel-btn-font-color', formValue.cancelBtnFontColor);
    root.style.setProperty('--save-btn-font-color', formValue.saveBtnFontColor);


    // 🔹 Calendar
    root.style.setProperty('--calendar-bg', formValue.calendarBg);
    root.style.setProperty('--calendar-text', formValue.calendarTextColor);
    root.style.setProperty('--calendar-header-bg', formValue.calendarHeaderBg);
    root.style.setProperty('--calendar-header-text', formValue.calendarHeaderText);
    root.style.setProperty('--calendar-selected', formValue.calendarSelectedColor);
    root.style.setProperty('--calendar-hover', formValue.calendarHoverColor);
    root.style.setProperty('--calendar-today', formValue.calendarTodayBorder);
    root.style.setProperty('--calendar-icon', formValue.calendarIconColor);
    root.style.setProperty('--calendar-disabled', formValue.calendarDisabledText);
  }

  isUserThemeApplied(): boolean {
    return document.body.classList.contains('user-theme');
  }

  clearUserThemeVariables() {
    const overlay = this.overlayContainer.getContainerElement();
    this.renderer.removeClass(document.body, 'user-theme');
    this.renderer.removeClass(overlay, 'user-theme');

    const root = document.documentElement;

    const vars = [
      // 🔹 Navbar
      '--navbar-bg',
      '--navbar-color',

      // 🔹 Tabs
      '--tab-bg',
      '--tab-color',
      '--tab-active-color',

      // 🔹 Table
      '--table-header-bg',
      '--table-header-color',
      '--table-color',
      '--table-body-color',
      '--table-body-bg',

      // 🔹 Footer
      '--footer-bg',
      '--footer-color',

      // 🔹 Buttons
      '--button-bg',
      '--button-color',

      // 🔹 Scrollbar
      '--scrollbar-color',

      // 🔹 Spinner
      '--spinner-bg',
      '--spinner-color',
      '--spinner-hover',

      // 🔹 Inputs / Controls
      '--checkbox-color',
      '--toggle-color',
      '--icon-color',

      // 🔹 Confirm Action Buttons
      '--cancel-btn-color',
      '--save-btn-color',
      '--cancel-btn-font-color',
      '--save-btn-font-color',
      '--dialog-bg',

      // 🔹 Calendar
      '--calendar-bg',
      '--calendar-text',
      '--calendar-header-bg',
      '--calendar-header-text',
      '--calendar-selected',
      '--calendar-hover',
      '--calendar-today',
      '--calendar-icon',
      '--calendar-disabled'
    ];

    vars.forEach(v => root.style.removeProperty(v));
  }
}
